/**
 * Pure helpers for formatted text inputs (masked phone, grouped price, …).
 * A mask is a template where `#` is a digit slot and every other character is
 * a literal — literals must not be digits, or extraction becomes ambiguous.
 */

/** Number of digit slots (`#`) in a mask. */
export function maskCapacity(mask: string): number {
  let count = 0;
  for (const char of mask) if (char === '#') count += 1;
  return count;
}

/**
 * Fill a mask's `#` slots with `digits`, emitting literals between them.
 * Output stops after the last consumed digit — no trailing literals, so a
 * partially typed value never shows dangling punctuation.
 */
export function applyMask(digits: string, mask: string): string {
  let out = '';
  let pending = '';
  let index = 0;
  for (const char of mask) {
    if (char !== '#') {
      pending += char;
      continue;
    }
    if (index >= digits.length) break;
    out += pending + digits[index];
    pending = '';
    index += 1;
  }
  return out;
}

/** Strip everything but digits. */
export function stripToDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/** Group an unsigned digit string in threes, e.g. `1234567` → `1 234 567`. */
export function groupDigits(digits: string, separator = ' '): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/**
 * Caret index in `text` just after its `count`-th digit (0 → index 0; more
 * digits than `text` has → `text.length`). Used to keep the caret anchored to
 * the digit the user just typed while separators reflow around it.
 */
export function caretIndexAfterDigits(text: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  let index = 0;
  for (const char of text) {
    index += 1;
    if (/\d/.test(char)) {
      seen += 1;
      if (seen === count) return index;
    }
  }
  return text.length;
}
